import { IProduct, OfferSubType } from '@jumpstart/core';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import { sentenceCase } from 'change-case';
import throttle from 'lodash/throttle';
import React, { useEffect, useState } from 'react';
import { IMAGE_PLACEHOLDER } from '../core/utils';
import { ElasticsearchService } from '../services/elasticsearch-service';
import { useAutocomplete } from '@mui/base/AutocompleteUnstyled';

export interface ISearchProducts {
  onMissing?(text: string): void;

  onSelect(product: IProduct): void;

  type?: OfferSubType;
  label?: string;
  defaultSearch?: string;
}

export default function SearchProducts(props: ISearchProducts) {
  const { onSelect, type, label, defaultSearch, onMissing } = { label: 'Search product', defaultSearch: 'A', ...props };
  const [options, setOptions] = useState<IProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [missing, setMissing] = useState(false);
  const [missingText, setMissingText] = useState('');

  const handleSelect = (selection: IProduct) => {
    onSelect(selection);
  };

  const {
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    inputValue
  } = useAutocomplete({
    id: 'pocket-scout-auto-search-bar',
    options: options,
    freeSolo: false,
    open: true,
    clearOnBlur: false,
    getOptionLabel: (option) => option.name
  });

  const fetch = React.useMemo(
    () =>
      throttle((request: { input: string; }, callback: (pages: IProduct[]) => void) => {
        const { input } = request;
        console.log('Running Fetch', input, 'Offer Type:', type);
        ElasticsearchService.products(type, input)
          .then((products: IProduct[]) => {
            return callback(products);
          })
          .catch((err: Error) => {
            console.error('Failed to get results', err.message);
          });
      }, 500),
    []
  );

  useEffect(() => {
    fetch({ input: inputValue ? inputValue : defaultSearch }, (products: IProduct[]) => {
      setOptions(products);
    });
  }, [inputValue]);

  useEffect(() => {
    fetch({ input: inputValue ? inputValue : defaultSearch }, (products: IProduct[]) => {
      setOptions(products);
    });
  }, []);

  const handleMissing = () => {
    console.log(missingText);
    if (onMissing) {
      onMissing(missingText);
    }
    setMissing(false);
  };

  return (
    <Grid container direction="column">
      <Dialog open={missing} onClose={() => setMissing(false)} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">Missing Product</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tell us the name of the product you couldn't find and we will find it for you
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            onChange={(e) => setMissingText(e.target.value)}
            id="name"
            label="Product Name"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMissing(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleMissing} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <TextField
        placeholder={label}
        variant="outlined"
        fullWidth
        autoFocus
        InputLabelProps={{
          ...getInputLabelProps()
        }}
        InputProps={{
          ...getInputProps() as any,
          endAdornment: (
            <React.Fragment>
              {loading ? <CircularProgress color="inherit" size={20}/> : null}
            </React.Fragment>
          )
        }}
        {...getInputProps() as any}
      />
      <List {...getListboxProps()}>
        {
          options.map((option: IProduct) =>
            <ListItem key={option.id} onClick={() => handleSelect(option)} button>
              <ListItemAvatar>
                <Avatar alt="" src={option.media ? option.media : IMAGE_PLACEHOLDER}/>
              </ListItemAvatar>
              <ListItemText
                primary={option.name}
                secondary={option.manufacturer ? sentenceCase(option.manufacturer) : ''}
              />
            </ListItem>
          )
        }
        {
          onMissing ?
            <ListItem onClick={() => setMissing(true)} button>
              <ListItemAvatar>
                <Avatar>
                  <HelpOutlineIcon/>
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary="Cant find what you are looking for?"
              />
            </ListItem>
            : null
        }
      </List>

    </Grid>
  );
}

